/**
 * MENU ORDER MANAGER
 *
 * Allows admins to customize the order of navigation menu items in the admin dashboard.
 * Features:
 * - Drag-and-drop reordering
 * - Per-admin customization (each admin has their own menu order)
 * - Persistent storage in database
 * - Reset to default functionality
 * - Optimistic UI updates
 * - Mobile-friendly touch support
 */

import React, { useState, useEffect } from 'react';
import { getServerSessionId } from '../../lib/serverSession';

interface MenuOrderManagerProps {
  currentUser: any;
  menuItems: Array<{ id: string; name: string; icon: string }>;
  onOrderChange?: (newOrder: string[]) => void;
  onConfigChange?: (config: {
    order: string[];
    disabledItems: string[];
    menuParents: Record<string, string | null>;
  }) => void;
}

interface DragState {
  draggedIndex: number | null;
  dragOverIndex: number | null;
}

export const MenuOrderManager: React.FC<MenuOrderManagerProps> = ({
  currentUser,
  menuItems,
  onOrderChange,
  onConfigChange
}) => {
  const [orderedItems, setOrderedItems] = useState(menuItems);
  const [dragState, setDragState] = useState<DragState>({ draggedIndex: null, dragOverIndex: null });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [disabledItems, setDisabledItems] = useState<string[]>([]);
  const [menuParents, setMenuParents] = useState<Record<string, string | null>>({});

  useEffect(() => {
    loadSavedOrder();
  }, [currentUser?.email]);

  const loadSavedOrder = async () => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    try {
      console.log('📋 [MENU-ORDER] Loading saved menu order for:', currentUser.email);
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/menu-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_saved_order', data: { adminEmail: currentUser.email } })
      });
      const result = await response.json();
      if (!result.success) {
        console.error('❌ [MENU-ORDER] Error loading menu order:', result.error);
        setLoading(false);
        return;
      }

      const data = result.menuData;
      if (data?.menu_order) {
        console.log('✅ [MENU-ORDER] Loaded saved order:', data.menu_order);
        // Reorder items based on saved order
        const savedOrder = data.menu_order as string[];
        const reordered = reorderItemsByIds(menuItems, savedOrder);
        setOrderedItems(reordered);
      } else {
        console.log('ℹ️ [MENU-ORDER] No saved order found, using default');
        setOrderedItems(menuItems);
      }

      if (data?.disabled_items) {
        setDisabledItems(data.disabled_items as string[]);
      } else {
        setDisabledItems([]);
      }

      if (data?.menu_parents) {
        setMenuParents(data.menu_parents as Record<string, string | null>);
      } else {
        setMenuParents({});
      }
    } catch (error) {
      console.error('❌ [MENU-ORDER] Error loading menu order:', error);
    } finally {
      setLoading(false);
    }
  };

  const reorderItemsByIds = (items: typeof menuItems, order: string[]) => {
    const itemMap = new Map(items.map(item => [item.id, item]));
    const reordered: typeof menuItems = [];

    // Add items in saved order
    order.forEach(id => {
      const item = itemMap.get(id);
      if (item) {
        reordered.push(item);
        itemMap.delete(id);
      }
    });

    // Add any new items that weren't in saved order
    itemMap.forEach(item => reordered.push(item));

    return reordered;
  };

  const handleDragStart = (index: number) => {
    setDragState({ draggedIndex: index, dragOverIndex: null });
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragState(prev => ({ ...prev, dragOverIndex: index }));
  };

  const handleDragLeave = () => {
    setDragState(prev => ({ ...prev, dragOverIndex: null }));
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const { draggedIndex } = dragState;

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragState({ draggedIndex: null, dragOverIndex: null });
      return;
    }

    // Reorder items
    const newItems = [...orderedItems];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);

    setOrderedItems(newItems);
    setDragState({ draggedIndex: null, dragOverIndex: null });

    // Auto-save after reordering
    saveOrder(newItems);
  };

  const handleDragEnd = () => {
    setDragState({ draggedIndex: null, dragOverIndex: null });
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= orderedItems.length) return;

    const newItems = [...orderedItems];
    const [moved] = newItems.splice(index, 1);
    newItems.splice(newIndex, 0, moved);
    setOrderedItems(newItems);
    saveConfig(newItems, disabledItems, menuParents);
  };

  const handlePositionChange = (index: number, value: string) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      return;
    }

    const maxPosition = orderedItems.length;
    const targetIndex = Math.min(parsed - 1, maxPosition - 1);

    const newItems = [...orderedItems];
    const [moved] = newItems.splice(index, 1);
    newItems.splice(targetIndex, 0, moved);
    setOrderedItems(newItems);
    saveConfig(newItems, disabledItems, menuParents);
  };

  const handleToggleEnabled = (id: string) => {
    setDisabledItems(prev => {
      const isDisabled = prev.includes(id);
      const next = isDisabled ? prev.filter(x => x !== id) : [...prev, id];
      saveConfig(orderedItems, next, menuParents);
      return next;
    });
  };

  const createsCycle = (itemId: string, parentId: string | null, parents: Record<string, string | null>) => {
    if (!parentId) return false;
    let current: string | null | undefined = parentId;
    while (current) {
      if (current === itemId) return true;
      current = parents[current] ?? null;
    }
    return false;
  };

  const handleParentChange = (id: string, parentId: string | null) => {
    setMenuParents(prev => {
      const next = { ...prev };
      if (!parentId || parentId === 'none') {
        delete next[id];
      } else if (!createsCycle(id, parentId, prev)) {
        next[id] = parentId;
      }
      saveConfig(orderedItems, disabledItems, next);
      return next;
    });
  };

  const getIndentLevel = (id: string, parents: Record<string, string | null>) => {
    let level = 0;
    let current = parents[id];
    while (current) {
      level += 1;
      current = parents[current] ?? null;
    }
    return level;
  };


  const saveOrder = async (items: typeof menuItems) => {
    await saveConfig(items, disabledItems, menuParents);
  };

  const saveConfig = async (
    items: typeof menuItems,
    disabled: string[],
    parents: Record<string, string | null>
  ) => {
    if (!currentUser?.email) {
      showMessage('error', 'User email not found');
      return;
    }

    try {
      setSaving(true);
      console.log('💾 [MENU-ORDER] Saving menu configuration for:', currentUser.email);

      const menuOrder = items.map(item => item.id);
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/menu-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_config',
          data: {
            adminEmail: currentUser.email,
            menuOrder,
            disabledItems: disabled,
            menuParents: parents
          }
        })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('❌ [MENU-ORDER] Error saving menu configuration:', result.error);
        showMessage('error', `Failed to save menu settings: ${result.error}`);
        // Rollback to previous order from DB
        loadSavedOrder();
        return;
      }

      console.log('✅ [MENU-ORDER] Menu configuration saved successfully');
      showMessage('success', 'Menu settings saved successfully!');

      // Notify parent component of changes
      if (onOrderChange) {
        onOrderChange(menuOrder);
      }
      if (onConfigChange) {
        onConfigChange({
          order: menuOrder,
          disabledItems: disabled,
          menuParents: parents
        });
      }
    } catch (error: any) {
      console.error('❌ [MENU-ORDER] Error saving menu configuration:', error);
      showMessage('error', `Failed to save menu settings: ${error.message}`);
      // Rollback to previous order
      loadSavedOrder();
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!confirm('Are you sure you want to reset the menu order to default? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      console.log('🔄 [MENU-ORDER] Resetting menu configuration to default for:', currentUser.email);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/menu-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'reset_to_default', data: { adminEmail: currentUser.email } })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('❌ [MENU-ORDER] Error resetting menu order:', result.error);
        showMessage('error', `Failed to reset menu settings: ${result.error}`);
        return;
      }

      console.log('✅ [MENU-ORDER] Menu configuration reset to default');
      const defaultOrder = menuItems.map(item => item.id);
      setOrderedItems(menuItems);
      setDisabledItems([]);
      setMenuParents({});
      showMessage('success', 'Menu settings reset to default!');

      // Notify parent component
      if (onOrderChange) {
        onOrderChange(defaultOrder);
      }
      if (onConfigChange) {
        onConfigChange({
          order: defaultOrder,
          disabledItems: [],
          menuParents: {}
        });
      }
    } catch (error: any) {
      console.error('❌ [MENU-ORDER] Error resetting menu order:', error);
      showMessage('error', `Failed to reset menu settings: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '12px',
        padding: '48px',
        border: '1px solid #374151',
        textAlign: 'center'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid #374151',
          borderTop: '3px solid #FFD700',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#ffffff', margin: 0 }}>Loading menu order...</p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#1F2937',
      borderRadius: '12px',
      padding: '32px',
      border: '1px solid #374151'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#FFFFFF',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>📋</span>
          Menu Order Manager
        </h2>
        <p style={{ color: '#ffffff', fontSize: '14px', margin: 0 }}>
          Use drag-and-drop, arrow buttons, position numbers, enable/disable toggles and parent selection to control your admin menu. Changes are saved automatically.
        </p>
      </div>

      {/* Success/Error Message */}
      {message && (
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '24px',
          backgroundColor: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${message.type === 'success' ? '#10B981' : '#EF4444'}`,
          color: message.type === 'success' ? '#10B981' : '#EF4444',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>{message.type === 'success' ? '✅' : '❌'}</span>
          <span>{message.text}</span>
        </div>
      )}

      {/* Reset Button */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleResetToDefault}
          disabled={saving}
          style={{
            padding: '10px 20px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444',
            border: '1px solid #EF4444',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.5 : 1,
            transition: 'all 0.2s ease'
          }}
        >
          🔄 Reset to Default
        </button>
      </div>

      {/* Menu Items List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {orderedItems.map((item, index) => {
          const isDragging = dragState.draggedIndex === index;
          const isDropTarget = dragState.dragOverIndex === index;
          const isDisabled = disabledItems.includes(item.id);
          const indentLevel = getIndentLevel(item.id, menuParents);

          return (
            <div
              key={item.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: isDragging
                  ? 'rgba(255, 215, 0, 0.15)'
                  : isDropTarget
                  ? 'rgba(255, 215, 0, 0.08)'
                  : '#111827',
                border: `2px solid ${isDragging
                  ? '#FFD700'
                  : isDropTarget
                  ? 'rgba(255, 215, 0, 0.5)'
                  : '#374151'}`,
                borderRadius: '8px',
                cursor: 'grab',
                opacity: isDisabled ? 0.5 : isDragging ? 0.6 : 1,
                transform: isDragging ? 'scale(1.01)' : 'scale(1)',
                transition: 'all 0.15s ease',
                userSelect: 'none'
              }}
            >
              {/* Drag Handle */}
              <div
                style={{
                  color: '#ffffff',
                  fontSize: '20px',
                  cursor: 'grab',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}
              >
                <span style={{ lineHeight: '4px' }}>⋮</span>
                <span style={{ lineHeight: '4px' }}>⋮</span>
              </div>

              {/* Position (editable) */}
              <input
                type="number"
                min={1}
                value={index + 1}
                onChange={e => handlePositionChange(index, e.target.value)}
                style={{
                  width: '56px',
                  height: '32px',
                  backgroundColor: '#111827',
                  borderRadius: '6px',
                  border: '1px solid #4B5563',
                  color: '#FBBF24',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
              />

              {/* Up/Down controls */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <button
                  onClick={() => moveItem(index, 'up')}
                  disabled={index === 0}
                  style={{
                    width: '28px',
                    height: '18px',
                    borderRadius: '4px',
                    border: '1px solid #4B5563',
                    backgroundColor: index === 0 ? '#111827' : '#1F2937',
                    color: index === 0 ? '#4B5563' : '#E5E7EB',
                    fontSize: '13px',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    lineHeight: 1
                  }}
                >
                  ↑
                </button>
                <button
                  onClick={() => moveItem(index, 'down')}
                  disabled={index === orderedItems.length - 1}
                  style={{
                    width: '28px',
                    height: '18px',
                    borderRadius: '4px',
                    border: '1px solid #4B5563',
                    backgroundColor: index === orderedItems.length - 1 ? '#111827' : '#1F2937',
                    color: index === orderedItems.length - 1 ? '#4B5563' : '#E5E7EB',
                    fontSize: '13px',
                    cursor: index === orderedItems.length - 1 ? 'not-allowed' : 'pointer',
                    lineHeight: 1
                  }}
                >
                  ↓
                </button>
              </div>

              {/* Icon + Name with indentation for nesting */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: indentLevel * 16
                }}
              >
                <span style={{ fontSize: '22px' }}>{item.icon}</span>
                <span
                  style={{
                    color: isDisabled ? '#9CA3AF' : '#FFFFFF',
                    fontSize: '16px',
                    fontWeight: 500
                  }}
                >
                  {item.name}
                </span>
              </div>

              {/* Parent selector */}
              <div style={{ minWidth: '180px' }}>
                <select
                  value={menuParents[item.id] ?? 'none'}
                  onChange={e => handleParentChange(item.id, e.target.value)}
                  style={{
                    width: '100%',
                    height: '32px',
                    backgroundColor: '#111827',
                    color: '#E5E7EB',
                    borderRadius: '6px',
                    border: '1px solid #4B5563',
                    fontSize: '13px'
                  }}
                >
                  <option value="none">No parent (top level)</option>
                  {orderedItems
                    .filter(potentialParent => potentialParent.id !== item.id)
                    .map(parent => (
                      <option key={parent.id} value={parent.id}>
                        {parent.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Enable/disable toggle */}
              <button
                onClick={() => handleToggleEnabled(item.id)}
                style={{
                  minWidth: '92px',
                  padding: '6px 10px',
                  borderRadius: '999px',
                  border: '1px solid',
                  borderColor: isDisabled ? '#6B7280' : '#10B981',
                  backgroundColor: isDisabled ? 'rgba(75, 85, 99, 0.2)' : 'rgba(16, 185, 129, 0.15)',
                  color: isDisabled ? '#D1D5DB' : '#6EE7B7',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isDisabled ? 'Disabled' : 'Enabled'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Saving Indicator */}
      {saving && (
        <div style={{
          marginTop: '24px',
          padding: '12px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid #3B82F6',
          borderRadius: '8px',
          color: '#3B82F6',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          💾 Saving menu order...
        </div>
      )}
    </div>
  );
};

